import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix default marker icons in webpack environments
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const STATUS_COLORS = {
  pending: '#F59E0B',
  reviewed: '#10B981',
  archived: '#64748B',
};

export default function ReportsMap({ reports, onSelect, selectedId }) {
  const [statusFilter, setStatusFilter] = React.useState({ pending: true, reviewed: true, archived: true });
  const filtered = useMemo(() => reports.filter(r => statusFilter[r.status] !== false), [reports, statusFilter]);
  const geoReports = useMemo(() => filtered.filter(r => typeof r.lat === 'number' && typeof r.lng === 'number'), [filtered]);

  // Default center: Tlaxcala state, Mexico
  const defaultCenter = [19.3181, -98.2375];
  const center = geoReports.length > 0 ? [geoReports[0].lat, geoReports[0].lng] : defaultCenter;
  const zoom = geoReports.length > 0 ? 7 : 6;

  const toggle = (k) => setStatusFilter(p => ({ ...p, [k]: !p[k] }));

  return (
    <div data-testid="reports-map" className="border-2 border-[#0B1F40] overflow-hidden" style={{ boxShadow: '6px 6px 0 #E50B15' }}>
      <div className="bg-[#0B1F40] text-white px-4 py-2 flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-[10px] font-black tracking-[0.2em] uppercase">MAPA DE DENUNCIAS</h3>
        <div className="flex items-center gap-2">
          {Object.entries(STATUS_COLORS).map(([k, v]) => (
            <button key={k} data-testid={`map-filter-${k}`} onClick={() => toggle(k)}
              className={`flex items-center gap-1 text-[9px] uppercase tracking-wider font-black px-1.5 py-0.5 transition-opacity ${statusFilter[k] ? 'opacity-100' : 'opacity-30 line-through'}`}>
              <span className="w-2 h-2 rounded-full" style={{ background: v }} />
              <span className="text-white/80">{k === 'pending' ? 'Pendiente' : k === 'reviewed' ? 'Revisada' : 'Archivada'}</span>
            </button>
          ))}
        </div>
      </div>
      <div style={{ height: 520, width: '100%' }}>
        <MapContainer center={center} zoom={zoom} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {geoReports.map(r => {
            const color = STATUS_COLORS[r.status] || '#E50B15';
            const isSelected = r.id === selectedId;
            return (
              <CircleMarker key={r.id} center={[r.lat, r.lng]}
                radius={isSelected ? 14 : 9}
                pathOptions={{ color: '#0B1F40', weight: 2, fillColor: color, fillOpacity: isSelected ? 1 : 0.75 }}
                eventHandlers={{ click: () => onSelect?.(r) }}
              >
                <Popup>
                  <div style={{ fontFamily: 'sans-serif', minWidth: 200 }}>
                    <p style={{ fontWeight: 900, color: '#E50B15', margin: '0 0 4px', fontSize: 12, letterSpacing: 1 }}>{r.ref_code}</p>
                    <p style={{ fontSize: 11, color: '#0B1F40', margin: '0 0 4px', fontWeight: 700 }}>
                      {[r.street, r.municipality, r.state].filter(Boolean).join(', ')}
                    </p>
                    <p style={{ fontSize: 11, color: '#475569', margin: '0 0 8px', lineHeight: 1.4 }}>
                      {r.description.slice(0, 140)}{r.description.length > 140 ? '…' : ''}
                    </p>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontSize: 9, background: color, color: 'white', padding: '2px 6px', fontWeight: 900, letterSpacing: 1 }}>
                        {r.status.toUpperCase()}
                      </span>
                      <span style={{ fontSize: 10, color: '#64748B' }}>
                        {(r.images?.length || 0)} img · {(r.videos?.length || 0)} vid
                      </span>
                    </div>
                    <button onClick={() => onSelect?.(r)}
                      style={{ background: '#0B1F40', color: 'white', border: 'none', padding: '6px 12px', fontWeight: 900, fontSize: 10, letterSpacing: 1, cursor: 'pointer', width: '100%' }}>
                      VER DETALLES
                    </button>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
      {geoReports.length === 0 && (
        <div className="p-3 bg-amber-50 border-t-2 border-amber-200 text-center">
          <p className="text-[10px] font-bold text-amber-900 tracking-wider">
            Ninguna denuncia con ubicación geolocalizada. Las denuncias nuevas se geolocalizan automáticamente.
          </p>
        </div>
      )}
    </div>
  );
}
